/**
 * 环境检测工具
 * 用于检测当前运行环境：微信小程序、微信浏览器、Android App、iOS App、普通浏览器
 */

// 存储检测结果
let _isWechatMiniProgram = false
let _isDetecting = false

/**
 * 检测是否在微信小程序环境中（使用官方推荐的方法）
 * @returns {boolean}
 */
export function isWechatMiniProgram() {
  if (typeof window === 'undefined') return false
  
  // 如果已经检测过，直接返回结果
  if (_isDetecting) {
    return _isWechatMiniProgram
  }
  
  // 方法1: 检查 __wxjs_environment（官方推荐，最可靠的方法）
  // 注意：只有在小程序 webview 中，这个值才会是 'miniprogram'
  // 在普通浏览器中，即使引入了 jweixin-1.3.2.js，这个值也不会是 'miniprogram'
  if (window.__wxjs_environment === 'miniprogram') {
    console.log('[环境检测] 通过 __wxjs_environment 检测到微信小程序环境')
    _isWechatMiniProgram = true
    return true
  }

  // 方法2: 使用 wx.miniProgram.getEnv()（官方推荐方法）
  // 注意：只有在 window.__wxjs_environment === 'miniprogram' 时，才调用这个方法
  // 因为 jweixin-1.3.2.js 在非小程序环境中也会注入 wx.miniProgram，但 getEnv 可能不可用
  if (typeof wx !== 'undefined' && wx.miniProgram && wx.miniProgram.getEnv) {
    try {
      wx.miniProgram.getEnv((res) => {
        if (res && res.miniprogram === true) {
          console.log('[环境检测] 通过 wx.miniProgram.getEnv() 检测到微信小程序环境')
          _isWechatMiniProgram = true
          // 更新 window.env
          if (window.env) {
            window.env.isWechatMiniProgram = true
            window.env.isWechatBrowser = false
            window.env.isNativeApp = false
            window.env.platform = 'miniprogram'
          }
        }
      })
    } catch (e) {
      console.warn('[环境检测] wx.miniProgram.getEnv() 调用失败:', e)
    }
  }

  // 方法3 和 4 不再使用，因为 jweixin-1.3.2.js 在非小程序环境中也会注入 wx.miniProgram
  // 只使用 window.__wxjs_environment === 'miniprogram' 作为唯一可靠判断标准
  
  // 如果之前检测到是小程序环境，但现在 __wxjs_environment 不是 'miniprogram'，则重置
  if (_isWechatMiniProgram && window.__wxjs_environment !== 'miniprogram') {
    console.log('[环境检测] 重置小程序环境检测（__wxjs_environment 不是 miniprogram）')
    _isWechatMiniProgram = false
    if (window.env) {
      window.env.isWechatMiniProgram = false
    }
  }

  return false
}

/**
 * 检测是否在微信浏览器中（非小程序）
 * @returns {boolean}
 */
export function isWechatBrowser() {
  if (typeof window === 'undefined') return false
  const ua = navigator.userAgent
  return ua.indexOf('MicroMessenger') > -1 && !isWechatMiniProgram()
}

/**
 * 检测是否在 Android 环境中
 * @returns {boolean}
 */
export function isAndroid() {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  return ua.indexOf('Android') > -1 || ua.indexOf('Adr') > -1
}

/**
 * 检测是否在 iOS 环境中
 * @returns {boolean}
 */
export function isIOS() {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  return !!ua.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/)
}

/**
 * 检测是否在原生 App 中（Android 或 iOS，排除微信环境）
 * @returns {boolean}
 */
export function isNativeApp() {
  return (isAndroid() || isIOS()) && !isWechatMiniProgram() && !isWechatBrowser()
}

/**
 * 获取当前运行平台
 * @returns {string} 'miniprogram' | 'wechat' | 'android' | 'ios' | 'web'
 */
export function getPlatform() {
  if (isWechatMiniProgram()) {
    return 'miniprogram'
  }
  if (isWechatBrowser()) {
    return 'wechat'
  }
  if (isAndroid()) {
    return 'android'
  }
  if (isIOS()) {
    return 'ios'
  }
  return 'web'
}

/**
 * 获取环境信息对象
 * @returns {Object}
 */
export function getEnvInfo() {
  return {
    isAndroid: isAndroid(),
    isIOS: isIOS(),
    isWechatMiniProgram: isWechatMiniProgram(),
    isWechatBrowser: isWechatBrowser(),
    isNativeApp: isNativeApp(),
    platform: getPlatform()
  }
}

// 按照微信官方文档推荐的方式检测微信小程序环境
// 在 WeixinJSBridgeReady 回调中检测
if (typeof window !== 'undefined') {
  // 初始化 window.env
  if (!window.env) {
    window.env = getEnvInfo()
  }
  
  // 官方推荐方法：在 WeixinJSBridgeReady 回调中检测
  function detectWechatMiniProgram() {
    // 方法1: 检查 __wxjs_environment（官方推荐）
    if (window.__wxjs_environment === 'miniprogram') {
      console.log('[环境检测] WeixinJSBridgeReady: 检测到微信小程序环境 (通过 __wxjs_environment)')
      _isWechatMiniProgram = true
      if (window.env) {
        window.env.isWechatMiniProgram = true
        window.env.isWechatBrowser = false
        window.env.isNativeApp = false
        window.env.platform = 'miniprogram'
      }
      return
    }
    
    // 方法2: 使用 wx.miniProgram.getEnv()（官方推荐）
    if (typeof wx !== 'undefined' && wx.miniProgram && wx.miniProgram.getEnv) {
      try {
        wx.miniProgram.getEnv((res) => {
          if (res.miniprogram) {
            console.log('[环境检测] WeixinJSBridgeReady: 检测到微信小程序环境 (通过 wx.miniProgram.getEnv())')
            _isWechatMiniProgram = true
            if (window.env) {
              window.env.isWechatMiniProgram = true
              window.env.isWechatBrowser = false
              window.env.isNativeApp = false
              window.env.platform = 'miniprogram'
            }
          }
        })
      } catch (e) {
        console.warn('[环境检测] wx.miniProgram.getEnv() 调用失败:', e)
      }
    }
  }
  
  // 监听 WeixinJSBridgeReady 事件（官方推荐方式）
  if (!window.WeixinJSBridge || !window.WeixinJSBridge.invoke) {
    document.addEventListener('WeixinJSBridgeReady', detectWechatMiniProgram, false)
  } else {
    // 如果已经 ready，直接检测
    detectWechatMiniProgram()
  }
  
  // 立即检测一次（如果已经加载完成）
  detectWechatMiniProgram()
  
  // 延迟检测（备用方案，确保能检测到）
  setTimeout(() => {
    detectWechatMiniProgram()
  }, 100)
  
  setTimeout(() => {
    detectWechatMiniProgram()
  }, 500)
  
  // 输出环境信息用于调试
  console.log('[环境检测] 当前环境信息:', window.env)
  console.log('[环境检测] window.__wxjs_environment:', window.__wxjs_environment)
  console.log('[环境检测] typeof wx:', typeof wx)
  console.log('[环境检测] wx.miniProgram:', typeof wx !== 'undefined' ? wx.miniProgram : 'undefined')
  console.log('[环境检测] userAgent:', navigator.userAgent)
}
